/**
 * ParagraphBlock: `paragraph` content block.
 *
 * data: { html }: rich-text HTML produced by the P5-3 editor.
 *
 * The HTML is author-supplied (semi-trusted ecosystem admins), so it is
 * sanitized with DOMPurify before being injected via `dangerouslySetInnerHTML`.
 * An unsanitized render would be a stored-XSS hole even with trusted authors.
 *
 * Degrades gracefully: empty / missing / non-string `html` renders nothing.
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

export default function ParagraphBlock({ data }) {
    const { html } = data || {};

    // Sanitize once per html value. DOMPurify strips scripts, event-handler
    // attributes, javascript: URLs, etc., leaving safe formatting markup.
    const safeHtml = useMemo(() => {
        if (typeof html !== 'string' || html.trim() === '') {
            return '';
        }
        return DOMPurify.sanitize(html);
    }, [html]);

    if (!safeHtml) {
        return null;
    }

    // safeHtml has been sanitized by DOMPurify above, so the
    // dangerouslySetInnerHTML injection below is safe.
    return (
        <div
            className="content-richtext"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
    );
}

ParagraphBlock.propTypes = {
    data: PropTypes.shape({
        html: PropTypes.string,
    }),
};

ParagraphBlock.defaultProps = {
    data: {},
};
